import _, {debounce} from "lodash";
import React, {useCallback, useEffect, useMemo, useState} from "react";
import {Link, useLocation, useNavigate, useParams} from "react-router-dom";
import {useAtom, useAtomValue, useSetAtom} from "jotai";
import {useTranslation} from "react-i18next";
import {
    Autocomplete,
    Divider,
    InputAdornment,
    ListItemButton,
    Stack,
    Switch,
    TextField,
    Tooltip,
    Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import {Farm} from "../../types/domain/farm";
import {Field} from "../../types/domain/field";
import {showActiveFieldsAtom, selectedEntityAtom} from "../../store/filterSelectionStore";
import {Crop} from "../../types/domain/crop";
import {useLoadableValueOrDefault} from "../../hooks/jotaiHooks";
import {activeDateLoadable, activeDateUserSelectionAtom} from "../../store/activeDateStore";
import {ResultsDatePicker} from "../base/ResultsDatePicker";
import {CompanyIcon} from "../icons/CompanyIcon";
import {WaterIcon} from "../icons/WaterIcon";
import {CropIcon} from "../icons/CropIcon";
import {useCrops} from "../../hooks/cropsHooks";
import {getAllFields, getCropIds} from "../../services/fieldService";
import {getAllFarms, getFarmsAlerts} from "../../services/farmService";
import {FarmList} from "./list/FarmList";
import dayjs from "dayjs";
import {CROPS} from "../../utils/enumItemsUtils";
import {isMaderaCompany} from "../../utils/maderaUtils";
import {YearsList} from "./list/YearsList";
import {useCompany, useParentCompany} from "../../hooks/companiesHooks";

export interface Props {
    basePath: "insights" | "map";
}

export const BaseFilterPanel: React.FC<Props> = ({basePath}) => {
    const {t} = useTranslation();
    const {companyId} = useParams();
    const [activeFieldsFilter, setActiveFieldsFilter] = useAtom(showActiveFieldsAtom);
    const date = useLoadableValueOrDefault(activeDateLoadable, null);
    const setDate = useSetAtom(activeDateUserSelectionAtom);
    const [fieldNameFilter, setFieldNameFilter] = useState<string>("");
    const [fieldCropFilter, setFieldCropFilter] = useState<string | undefined>(undefined);
    const selectedEntity = useAtomValue(selectedEntityAtom);
    const {cropsById} = useCrops();
    const [parentCompanyFarms, setParentCompanyFarms] = useState<Farm[] | undefined>(undefined);
    const [lastLoadedParentCompanyId, setLastLoadedParentCompanyId] = useState("");
    const [farmsAlerts, setFarmsAlerts] = useState({});
    const [crops, setCrops] = useState<Crop[] | undefined>(undefined);
    const [foundFieldsByFarmId, setFoundFieldsByFarmId] = useState({});
    const [activeFieldsFilterAtLastLoad, setActiveFieldsFilterAtLastLoad] = useState<boolean>(activeFieldsFilter);
    const company = useCompany();
    const parentCompany = useParentCompany();
    const navigate = useNavigate();
    const location = useLocation();
    const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search]);

    useEffect(() => {
        if (!companyId && company?.data?.id) {
            navigate({
                pathname: company?.data?.id,
                search: searchParams.toString(),
            });
        }

        if (!parentCompany?.data && company?.data?.id) {
            searchParams.set("parentCompany", company?.data?.id);
            navigate({
                pathname: location.pathname,
                search: searchParams.toString(),
            });
        }
    }, [company, companyId, location.pathname, navigate, parentCompany, searchParams]);

    useEffect(() => {
        async function loadFarmsAlerts() {
            if (companyId && date) {
                const farmsAlerts = await getFarmsAlerts(companyId, dayjs(date).format("YYYY-MM-DD"));
                setFarmsAlerts(farmsAlerts);
            }
        }

        async function loadCrops() {
            if (companyId) {
                const crops = await getCropIds(companyId);

                setCrops(
                    crops.map((cropId) => {
                        const crop = CROPS.find((CROP) => CROP.id === cropId);
                        return {id: cropId, name: t("enumerations.crops." + crop?.i18nKey) ?? ""};
                    }),
                );
            }
        }
        void loadFarmsAlerts();
        void loadCrops();
    }, [companyId, date, t]);

    useEffect(() => {
        if (!parentCompany?.data?.id) return;

        async function loadparentCompanyFields() {
            if (
                parentCompany?.data?.id &&
                (lastLoadedParentCompanyId !== parentCompany?.data?.id ||
                    activeFieldsFilter !== activeFieldsFilterAtLastLoad)
            ) {
                setActiveFieldsFilterAtLastLoad(activeFieldsFilter);
                setLastLoadedParentCompanyId(parentCompany?.data?.id);

                const _foundFarms = await getAllFarms(parentCompany.data.id, activeFieldsFilter, true);

                setParentCompanyFarms(_foundFarms);
            }
        }

        void loadparentCompanyFields();
    }, [lastLoadedParentCompanyId, activeFieldsFilter, activeFieldsFilterAtLastLoad, parentCompany?.data?.id]);

    const uniqueFarms = useMemo(() => {
        return _.uniqBy(parentCompanyFarms, "name");
    }, [parentCompanyFarms]);

    const searchFields = useCallback(() => {
        async function getFields() {
            if (companyId && fieldNameFilter?.length > 2) {
                const fields = await getAllFields({
                    companyId,
                    cropId: fieldCropFilter,
                    onlyActiveOrders: activeFieldsFilter,
                    name: fieldNameFilter,
                    attributes: ["name", "id", "farmId", "companyId"],
                });

                const groupedFields = fields.reduce<Record<string, Field[]>>((acc, field) => {
                    if (!field.farmId) return acc;
                    if (!acc[field.farmId]) acc[field.farmId] = [];

                    acc[field.farmId].push(field);

                    return acc;
                }, {});

                setFoundFieldsByFarmId(groupedFields);
            }
        }

        void getFields();
    }, [companyId, fieldNameFilter, fieldCropFilter, activeFieldsFilter]);

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const searchFieldsDebounced = useCallback(
        debounce(() => {
            if (fieldNameFilter?.length > 2) {
                searchFields();
            }
        }, 300),
        [searchFields],
    );

    useEffect(() => {
        searchFieldsDebounced();
        return () => searchFieldsDebounced.cancel();
    }, [fieldNameFilter, searchFieldsDebounced]);

    const isSearching = useMemo(() => {
        // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
        return Boolean(fieldCropFilter || searchFields?.length || fieldNameFilter?.length > 2);
    }, [fieldCropFilter, fieldNameFilter?.length, searchFields?.length]);

    useEffect(() => {
        searchFields();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [fieldCropFilter, activeFieldsFilter]);

    const selectedCrop = useMemo(() => {
        return fieldCropFilter ? cropsById[fieldCropFilter] : null;
    }, [fieldCropFilter, cropsById]);

    const isActiveCompanySelected = selectedEntity !== null && company?.data?.id === selectedEntity?.entityId;

    return (
        <Stack direction="column" height="100%" overflow="hidden">
            <Stack direction="column" sx={{height: "calc(100% - 2.25rem)", padding: "1.125rem"}} overflow="hidden">
                <ResultsDatePicker
                    date={date}
                    onDateChange={setDate}
                    sx={{width: "100%"}}
                    disableElevation
                    disabled={date === null}
                />
                {companyId && (
                    <Stack direction="column" marginTop="1rem">
                        <Typography textTransform="uppercase" sx={{color: "#737990"}}>
                            {t("base_filter_panel.company_filter.name")}
                        </Typography>
                        <Divider sx={{marginY: "0.3rem"}} />
                        <ListItemButton
                            sx={{
                                paddingX: 0,
                                height: "36px",
                                maxHeight: "36px",
                                "&.Mui-selected, &.Mui-selected:hover": {
                                    backgroundColor: "#5161C1",
                                    color: "white",
                                },
                            }}
                            disableRipple
                            selected={isActiveCompanySelected}
                            component={Link}
                            to={{pathname: `/${basePath}/${parentCompany?.data?.id}`, search: location.search}}
                        >
                            <Stack direction="row" gap={1} paddingX="0.75rem" minWidth="0" alignItems="center">
                                <CompanyIcon
                                    sx={{
                                        stroke: isActiveCompanySelected ? "#DBDEE6" : "#6476CB",
                                        strokeWidth: "2",
                                        width: "1rem",
                                        height: "1rem",
                                    }}
                                />
                                <Typography textOverflow="ellipsis" noWrap>
                                    {parentCompany?.data?.properties?.name ?? company?.data?.properties?.name}
                                </Typography>
                            </Stack>
                        </ListItemButton>
                    </Stack>
                )}
                {companyId && (
                    <Stack direction="column" marginTop="1rem" overflow="hidden">
                        <Stack
                            direction="row"
                            alignItems="center"
                            justifyContent="space-between"
                            gap="1rem"
                            width="100%"
                        >
                            <Typography textTransform="uppercase" sx={{color: "#737990"}}>
                                {t("base_filter_panel.farm_fields_filter.name")}
                            </Typography>

                            <Tooltip title={t("base_filter_panel.farm_fields_filter.filter_active_fields")}>
                                <Switch
                                    checked={activeFieldsFilter}
                                    onChange={(_, v) => {
                                        setActiveFieldsFilter(v);
                                    }}
                                />
                            </Tooltip>
                            <Stack direction="row" alignItems="center" gap="0.5rem" marginRight={1}>
                                <WaterIcon
                                    sx={{
                                        stroke: "#5161C1",
                                        width: "0.9rem",
                                        height: "0.9rem",
                                    }}
                                />
                                <CropIcon
                                    sx={{
                                        stroke: "#00853B",
                                        width: "0.9rem",
                                        height: "0.9rem",
                                    }}
                                />
                            </Stack>
                        </Stack>
                        <Divider sx={{marginBottom: "0.75rem", marginTop: "0.3rem"}} />
                        <Stack direction="column" gap={1.5} overflow="hidden">
                            <Autocomplete
                                fullWidth
                                size="small"
                                options={crops ?? []}
                                autoComplete={true}
                                autoHighlight={true}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        variant="filled"
                                        hiddenLabel
                                        margin="none"
                                        size="small"
                                        type="text"
                                        placeholder={t("base_filter_panel.farm_fields_filter.placeholder_by_crop")}
                                        fullWidth
                                        InputProps={{
                                            ...params.InputProps,
                                            disableUnderline: true,
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    <Stack padding="0.25rem">
                                                        <CropIcon
                                                            sx={{
                                                                stroke: "#404DB8",
                                                                width: "1rem",
                                                                height: "1rem",
                                                            }}
                                                        />
                                                    </Stack>
                                                </InputAdornment>
                                            ),
                                            sx: {
                                                paddingLeft: "0.5rem !important",
                                                "& .MuiInputBase-input": {
                                                    marginLeft: "-0.7rem !important",
                                                },
                                            },
                                        }}
                                    />
                                )}
                                getOptionLabel={(item: Crop) => _.capitalize(item.name)}
                                value={selectedCrop}
                                onChange={(_, newValue) => {
                                    setFieldCropFilter(newValue?.id);
                                }}
                            />
                            <TextField
                                InputProps={{
                                    disableUnderline: true,
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <SearchIcon sx={{color: "#404DB8"}} />
                                        </InputAdornment>
                                    ),
                                    sx: {
                                        paddingLeft: "0.5rem !important",
                                        "& .MuiInputBase-input": {
                                            marginLeft: "-0.7rem !important",
                                        },
                                    },
                                }}
                                hiddenLabel
                                margin="none"
                                size="small"
                                id="fieldFilterByName"
                                data-test="fieldFilterByName"
                                type="search"
                                fullWidth
                                variant="filled"
                                placeholder={t("base_filter_panel.farm_fields_filter.placeholder_by_name")}
                                value={fieldNameFilter}
                                onChange={(event) => setFieldNameFilter(event.target.value)}
                            />

                            {parentCompany?.data && isMaderaCompany(parentCompany?.data) ? (
                                <YearsList
                                    parentCompany={parentCompany?.data}
                                    activeFieldsFilter={activeFieldsFilter}
                                    farms={uniqueFarms ?? []}
                                    farmsAlerts={farmsAlerts}
                                    companyId={companyId}
                                    fieldNameFilter={fieldNameFilter}
                                    foundFieldsByFarmId={foundFieldsByFarmId}
                                    isSearching={isSearching}
                                    fieldCropFilter={fieldCropFilter}
                                    basePath={basePath}
                                ></YearsList>
                            ) : (
                                <FarmList
                                    farms={uniqueFarms ?? []}
                                    farmsAlerts={farmsAlerts}
                                    companyId={companyId}
                                    fieldNameFilter={fieldNameFilter}
                                    foundFieldsByFarmId={foundFieldsByFarmId}
                                    isSearching={isSearching}
                                    fieldCropFilter={fieldCropFilter}
                                    activeFieldsFilter={activeFieldsFilter}
                                    basePath={basePath}
                                    parentCompany={parentCompany?.data}
                                ></FarmList>
                            )}
                        </Stack>
                    </Stack>
                )}
            </Stack>
        </Stack>
    );
};
